<script lang="ts">
import Vue from 'vue'
import {Component, Prop, Watch} from 'vue-property-decorator'
import {Call, Device} from '@twilio/voice-sdk'

import api from '@/api'
import {AxiosResponse} from 'axios'

@Component({})
export default class SpaceVoiceCaller extends Vue {
  @Prop()
  spaceId: string

  device: Device = null
  call: Call = null
  callQualityWarningName: string = ''
  lastVolumeDate: Date = null
  callStartDate: Date = null
  inputVolume = 0
  outputVolume = 0

  showCallConfirmationModal = false
  callQualityTooltipID = 'call-quality-tooltip-' + Date.now()

  get showCallbar(): boolean {
    return !!this.call
  }

  get callDuration(): string {
    // Returns call duration in format "mm:ss"
    //console.log('callDuration', this.callStartDate, this.lastVolumeDate)
    if (!this.callStartDate || !this.lastVolumeDate) return '0:00'

    let seconds = (this.lastVolumeDate.getTime() - this.callStartDate.getTime()) / 1000
    let text = new Date(seconds * 1000).toISOString().slice(14, 19)
    return text.startsWith('00:') ? text.slice(1, 5) : text
  }

  mounted() {}

  confimationModalOk() {
    this.showCallConfirmationModal = false
    this.makeCall()
  }

  async makeCall() {
    /**
     * https://www.twilio.com/docs/voice/sdks/javascript/twiliocall
     * https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice
     */
    let response: AxiosResponse

    // Get twilio call token
    try {
      response = await api.spaces.getVoiceToken(this.spaceId)
    } catch (err) {
      ;(this as any).$bvToast.toast(`Call error: ${err}`, {
        toaster: 'b-toaster-top-center',
        variant: 'danger',
      })
      return
    }

    window.DEVICE = this.device = new Device(response.data.accessToken)

    console.info('device', this.device)

    this.device.on('error', (error) => {
      console.error('Twilio error:', error)
      ;(this as any).$bvToast.toast(`Call error: ${error}`, {
        toaster: 'b-toaster-top-center',
        variant: 'danger',
      })
    })

    window.CALL = this.call = await this.device.connect()

    this.call.on('accept', (call) => {
      console.warn('Call event - accept', call)
      this.callStartDate = new Date()
      this.$forceUpdate()
    })

    this.call.on('volume', (inputVolume, outputVolume) => {
      this.lastVolumeDate = new Date()
      this.inputVolume = inputVolume
      this.outputVolume = outputVolume
      this.$forceUpdate()
    })

    this.call.on('warning', (warningName, warningData) => {
      console.error('Call quality warning:', warningName, warningData)
      this.callQualityWarningName = warningName
    })

    this.call.on('warning-cleared', (warningName) => {
      console.error('Call quality warning cleared:', warningName)
      this.callQualityWarningName = ''
    })

    this.call.on('error', (twilioError) => {
      console.error('Call error:', twilioError)
      ;(this as any).$bvToast.toast(`Call error: ${twilioError}`, {
        toaster: 'b-toaster-top-center',
        variant: 'danger',
      })
    })

    this.call.on('disconnect', (call) => {
      console.warn('Call event - disconnect', call)
      this.hangUp()
    })

    window.onbeforeunload = function (e) {
      // Custom msg only shows on older browsers, otherwise a generic message.
      return "There's a call in progress, if you leave this page the call will end. Are you sure?"
    }
  }

  async hangUp() {
    if (this.device) {
      this.device.destroy()
    }
    window.onbeforeunload = null

    // Reset vue component state to initial.
    // @ts-ignore
    Object.assign(this.$data, this.$options.data.apply(this))
  }
}
</script>

<template>
  <div>
    <b-btn v-if="call" @click="hangUp()" variant="primary">End Call</b-btn>
    <b-btn v-else @click="showCallConfirmationModal = true" variant="primary" class="py-2 px-3">
      <img src="@/assets/icons/phone.svg" class="fa-fw mr-1 mb-1" style="width: 15px" />
      Call Smart Hub
    </b-btn>

    <div v-if="call" class="mt-4">
      <label>Microphone Volume</label>
      <div class="volume volume__input" :style="{width: `${inputVolume * 100}%`}"></div>
      <br />
      <label>Incoming Volume</label>
      <div class="volume volume__output" :style="{width: `${outputVolume * 100}%`}"></div>
    </div>

    <div
      v-if="showCallbar"
      class="call-bar pulsating-bg position-fixed fixed-bottom animated slideInUp row mx-0">
      <div class="mx-auto text-center">
        <font-awesome-icon
          :id="callQualityTooltipID"
          v-if="callQualityWarningName"
          icon="exclamation-triangle"
          class="fa-fw text-light mr-3 mt-1" />

        <b-popover :target="callQualityTooltipID" :show="callQualityWarningName" placement="top">
          <div class="p-2" style="font-size: 110%">
            Network is experiencing connectivity issues. This may result in poor call quality.
            {{ '[' }}{{ callQualityWarningName }}{{ ']' }}
          </div>
        </b-popover>

        <img src="@/assets/icons/phone.svg" class="fa-fw mr-2 mb-1" style="width: 15px" />
        <span>Calling Smart Hub</span>
        <span class="px-2">-</span>
        <span class="text-monospace">{{ callDuration }}</span>

        <b-btn variant="light" @click="hangUp()" class="hangup ml-4">End Call</b-btn>
      </div>
    </div>

    <b-modal v-model="showCallConfirmationModal" centered ok-only>
      <h2>Call Smart Hub?</h2>
      <p class="mt-3">
        Calling the Smart Hub will connect with the care recipient's device in their home.
      </p>
      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="confimationModalOk()" class="btn-block" size="lg">
          Okay
        </b-btn>
      </template>
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/css/_variables.scss';
@import '@/assets/css/_mixins.scss';

.volume {
  height: 20px;
  background-color: #888;
  border-radius: 10px;

  &__input {
  }
}

.call-bar {
  width: 100%;
  color: white;
  padding: 1.35rem;
  font-size: 110%;

  .hangup {
    border-radius: 5px;
    color: $info;
    padding: 0.275rem 0.75rem;
  }
}

.pulsating-bg {
  animation: pulsatingBg 3s infinite ease-in-out;
}
@keyframes pulsatingBg {
  0% {
    background-color: $info;
  }
  50% {
    background-color: darken($info, 8%);
  }
  100% {
    background-color: $info;
  }
}
</style>
